import Dexie from 'dexie';

export const db = new Dexie('TresorDatabase');

/** Maximum folders per vault (flat limit across all levels). */
export const MAX_VAULT_FOLDERS = 64;

/** Maximum characters for a folder name (display + journal payload). */
export const MAX_FOLDER_NAME_LENGTH = 80;

/** Consistent folder key for root vs. Dexie auto-increment subfolder ids. */
export function normalizeFolderId(id) {
  if (id === null || id === undefined || id === '' || id === 'root') return 'root';
  return String(id);
}

const SIZE_UNIT_MULT = {
  BYTES: 1,
  KB: 1024,
  MB: 1024 ** 2,
  GB: 1024 ** 3,
  TB: 1024 ** 4
};

/** Numeric byte size for sorting (uses sizeBytes when present, else parses display string). */
export function getFileSizeBytes(file) {
  if (typeof file?.sizeBytes === 'number' && file.sizeBytes >= 0) return file.sizeBytes;
  if (!file?.size || typeof file.size !== 'string') return 0;
  const match = file.size.trim().match(/^([\d.,]+)\s*(Bytes|KB|MB|GB|TB)$/i);
  if (!match) return 0;
  const value = parseFloat(match[1].replace(',', '.'));
  const unit = match[2].toUpperCase();
  return Math.round(value * (SIZE_UNIT_MULT[unit] || 1));
}

function createClientId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `legacy-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

db.version(2).stores({
  folders: '++id, parentId, name, createdAt',
  files: '++id, folderId, name, type, size, createdAt',
  fileBlobs: 'id',
  keyStore: 'id',
  pendingUploads: 'id, fileName, totalSize, uploadedChunks, totalChunks, uploadId'
});

db.version(3).stores({
  folders: '++id, parentId, name, createdAt',
  files: '++id, folderId, name, type, size, createdAt',
  fileBlobs: 'id',
  keyStore: 'id',
  pendingUploads: 'id, fileName, uploadId, uploadedChunks, totalChunks',
  pendingFileBlobs: 'id'
});

// v4: multi-device sync — stable clientIds + cloud upload metadata + journal cursor
db.version(4).stores({
  folders: '++id, clientId, parentClientId, parentId, name, createdAt',
  files: '++id, clientId, folderClientId, folderId, name, type, size, createdAt, encryptionId, uploadId',
  fileBlobs: 'id',
  keyStore: 'id',
  pendingUploads: 'id, fileName, uploadId, uploadedChunks, totalChunks',
  pendingFileBlobs: 'id',
  meta: 'key'
}).upgrade(async (tx) => {
  const folders = await tx.table('folders').toArray();
  const idToClient = new Map();

  for (const folder of folders) {
    if (!folder.clientId) folder.clientId = createClientId();
    idToClient.set(folder.id, folder.clientId);
  }

  for (const folder of folders) {
    if (!folder.parentClientId) {
      const pid = normalizeFolderId(folder.parentId);
      folder.parentClientId = pid === 'root' ? 'root' : (idToClient.get(Number(pid)) || 'root');
    }
    await tx.table('folders').put(folder);
  }

  const files = await tx.table('files').toArray();
  for (const file of files) {
    if (!file.clientId) file.clientId = createClientId();
    if (!file.folderClientId) {
      const fid = normalizeFolderId(file.folderId);
      file.folderClientId = fid === 'root' ? 'root' : (idToClient.get(Number(fid)) || 'root');
    }
    await tx.table('files').put(file);
  }
});

// v5: schema identical to v4 — pendingUploads keeps `id` (Dexie cannot change primary keys).
// uploads.js supplies id explicitly (uploadId from server) on .add().
db.version(5).stores({
  folders: '++id, clientId, parentClientId, parentId, name, createdAt',
  files: '++id, clientId, folderClientId, folderId, name, type, size, createdAt, encryptionId, uploadId',
  fileBlobs: 'id',
  keyStore: 'id',
  pendingUploads: 'id, fileName, uploadId, uploadedChunks, totalChunks',
  pendingFileBlobs: 'id',
  meta: 'key'
});
